/**
 * The "this demo has ended" state (#2824, #2985).
 *
 * The API answers a demo request with a 401 and the header `X-Demo-Ended: 1`
 * when the access gate stopped the demo (mode Closed, or the visitor's key
 * revoked). The fetch layer calls `signalDemoEnded()` for that answer in a demo
 * tab. The demo auth providers then show one full-page screen
 * (`DemoEndedScreen`) in place of the app.
 *
 * The phone frame runs the member app in an iframe. A signal there goes to the
 * parent page with a message, so the parent shows the screen and the frame goes
 * with it.
 */

import { useSyncExternalStore } from "react";

import { inDemoPhoneFrame } from "./demoPhone";
import { clearDemoSession, isDemoTab } from "./demoSession";

export const DEMO_ENDED_HEADER = "x-demo-ended";
export const DEMO_ENDED_MESSAGE = "tarii-demo-ended";

let ended = false;
// True once this tab got a 2xx for a demo-authenticated call. Only a demo that was live
// in the tab can "end" there. A stored demo that was already dead at mount is stale.
let liveInTab = false;
const listeners = new Set<() => void>();

function setEnded(): void {
  if (ended) return;
  ended = true;
  for (const listener of [...listeners]) listener();
}

/** A demo-authenticated call got a 2xx in this tab: the demo is live here. */
export function markDemoLiveInTab(): void {
  if (isDemoTab()) liveInTab = true;
}

/** A demo request was refused because the demo ended. No-op outside a demo tab. */
export function signalDemoEnded(): void {
  if (!isDemoTab()) return;
  // The public demo pages (`/demo`, `/demo/<team>`) are where the visitor goes next.
  // A stale stored demo clears itself there, as before, with no ending screen.
  const path = window.location.pathname;
  if (path === "/demo" || path.startsWith("/demo/")) return;
  if (!liveInTab) {
    // A stale stored demo (a revoked key, a closed gate) that another tab left behind.
    // Drop it silently and reload, so the tab falls back to its normal sign-in. The dev
    // account id stays: `clearDemoSession` does not touch it.
    if (inDemoPhoneFrame()) return;
    clearDemoSession();
    window.location.assign(window.location.href);
    return;
  }
  if (inDemoPhoneFrame()) {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: DEMO_ENDED_MESSAGE }, window.location.origin);
      }
    } catch {
      // No parent page: the frame has nothing to tell.
    }
  }
  setEnded();
}

export function isDemoEndedSignalled(): boolean {
  return ended;
}

/** Test helper: forget the state. */
export function resetDemoEndedSignal(): void {
  ended = false;
  liveInTab = false;
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True once the demo in this tab has ended. */
export function useDemoEnded(): boolean {
  return useSyncExternalStore(subscribe, isDemoEndedSignalled, () => false);
}

// The parent page hears the frame.
if (typeof window !== "undefined") {
  window.addEventListener("message", (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;
    if ((e.data as { type?: string } | null)?.type !== DEMO_ENDED_MESSAGE) return;
    if (!isDemoTab() || inDemoPhoneFrame() || !liveInTab) return;
    setEnded();
  });
}
